import React, { PureComponent } from 'react';
import ReactDataSheet from 'react-datasheet';
import {
  ENTER_KEY,
  ESCAPE_KEY,
  TAB_KEY,
  RIGHT_KEY,
  LEFT_KEY,
  UP_KEY,
  DOWN_KEY,
} from './keys';

import { CustomDataEditor } from './dataeditor';
import { DefaultValueViewer } from './default_components';
import { renderData, renderValue } from './renderhelper';
import { GridElement, Value } from './types';
import ValueRenderer = ReactDataSheet.ValueRenderer;
import DataRenderer = ReactDataSheet.DataRenderer;
import CellRenderer = ReactDataSheet.CellRenderer;
import AttributesRenderer = ReactDataSheet.AttributesRenderer;
import ValueViewer = ReactDataSheet.ValueViewer;
import DataEditor = ReactDataSheet.DataEditor;


type InitialDataProps = {
  cell: GridElement;
  row: number;
  col: number;
  valueRenderer: ValueRenderer<GridElement>;
  dataRenderer: DataRenderer<GridElement>;
};

type InitialValueProps = {
  cell: GridElement;
  row: number;
  col: number;
  valueRenderer: ValueRenderer<GridElement>;
};

function initialData({ cell, row, col, valueRenderer, dataRenderer }: InitialDataProps) {
  return renderData(cell, row, col, valueRenderer, dataRenderer);
}

function initialValue({ cell, row, col, valueRenderer }: InitialValueProps) {
  return renderValue(cell, row, col, valueRenderer);
}

function widthStyle(cell: GridElement) {
  const width = typeof cell.width === 'number' ? cell.width + 'px' : cell.width;
  return width ? { width } : null;
}

export interface DataCellProps {
  row: number;
  col: number;
  cell: GridElement;
  forceEdit?: boolean;
  selected?: boolean;
  editing?: boolean;
  clearing?: boolean;
  cellRenderer: CellRenderer<GridElement>;
  valueRenderer: ValueRenderer<GridElement>;
  dataRenderer: DataRenderer<GridElement>;
  valueViewer: ValueViewer<GridElement>;
  dataEditor: DataEditor<GridElement>;
  attributesRenderer: AttributesRenderer<GridElement>;
  onNavigate: (e: React.KeyboardEvent<HTMLElement>, commit: boolean) => boolean;
  onMouseDown: (row: number, col: number, e:React.MouseEvent<HTMLElement>) => void;
  onMouseOver: (row: number, col: number) => void;
  onDoubleClick: (row: number, col:number) => void;
  onContextMenu: (e: React.MouseEvent<HTMLElement>, row: number, col: number) => void;
  onChange: (row: number, col: number, value: Value) => void;
  onRevert: () => void;
  onKeyUp: (e: React.KeyboardEvent<HTMLElement>) => void;
}

type State = {
  updated: boolean,
  reverting: boolean,
  committing: boolean,
  value: Value,
};

export class DataCell extends PureComponent<DataCellProps, State> {
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  private timeout: any;

  constructor(props: DataCellProps) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.handleCommit = this.handleCommit.bind(this);
    this.handleRevert = this.handleRevert.bind(this);

    this.handleKey = this.handleKey.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseOver = this.handleMouseOver.bind(this);
    this.handleContextMenu = this.handleContextMenu.bind(this);
    this.handleDoubleClick = this.handleDoubleClick.bind(this);

    this.state = {
      updated: false,
      reverting: false,
      committing: false,
      value: '',
    };
  }

  componentDidUpdate(prevProps: Readonly<DataCellProps>) {
    if (
      !this.props.cell.disableUpdatedFlag
            && initialValue(prevProps) !== initialValue(this.props)
    ) {
      this.setState({ updated: true });
      this.timeout = setTimeout(() => this.setState({ updated: false }), 700);
    }
    if (this.props.editing === true && prevProps.editing === false) {
      const value = this.props.clearing ? '' : initialData(this.props);
      this.setState({ value, reverting: false });
    }

    if (
      prevProps.editing === true
            && this.props.editing === false
            && !this.state.reverting
            && !this.state.committing
            && this.state.value !== initialData(this.props)
    ) {
      this.props.onChange(this.props.row, this.props.col, this.state.value);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  handleChange(value: Value) {
    this.setState({ value, committing: false });
  }

  handleCommit(value: Value, e: React.KeyboardEvent<HTMLElement>) {
    const { onChange, onNavigate } = this.props;
    if (value !== initialData(this.props)) {
      this.setState({ value, committing: true });
      onChange(this.props.row, this.props.col, value);
    } else {
      this.handleRevert();
    }
    if (e) {
      e.preventDefault();
      onNavigate(e, true);
    }
  }

  handleRevert() {
    this.setState({ reverting: true });
    this.props.onRevert();
  }

  handleMouseDown(e: React.MouseEvent<HTMLElement>) {
    const { row, col, onMouseDown, cell } = this.props;
    if (!cell.disableEvents) {
      onMouseDown(row, col, e);
    }
  }

  handleMouseOver() {
    const { row, col, onMouseOver, cell } = this.props;
    if (!cell.disableEvents) {
      onMouseOver(row, col);
    }
  }

  handleDoubleClick() {
    const { row, col, onDoubleClick, cell } = this.props;
    if (!cell.disableEvents) {
      onDoubleClick(row, col);
    }
  }

  handleContextMenu(e: React.MouseEvent<HTMLElement>) {
    const { row, col, onContextMenu, cell } = this.props;
    if (!cell.disableEvents) {
      onContextMenu(e, row, col);
    }
  }

  handleKey(e: React.KeyboardEvent<HTMLElement>) {
    const keyCode = e.which || e.keyCode;
    if (keyCode === ESCAPE_KEY) {
      this.handleRevert();
      return;
    }
    const {
      cell: { component },
      forceEdit,
    } = this.props;
    const eatKeys = forceEdit || !!component;
    const commit = keyCode === ENTER_KEY
            || keyCode === TAB_KEY
            || (!eatKeys && [LEFT_KEY, RIGHT_KEY, UP_KEY, DOWN_KEY].includes(keyCode));

    if (commit) {
      this.handleCommit(this.state.value, e);
    }
  }

  renderComponent(editing: boolean, cell: GridElement) {
    const { component, readOnly, forceComponent } = cell;
    if ((editing && !readOnly) || forceComponent) {
      return component;
    }
    return undefined;
  }

  renderEditor(editing: boolean, cell: GridElement, row: number, col: number, dataEditor: DataEditor<GridElement>) {
    if (editing) {
      const Editor = cell.dataEditor || dataEditor || CustomDataEditor;
      return (
                <Editor
                    cell={cell}
                    row={row}
                    col={col}
                    type={cell.type}
                    value={this.state.value}
                    onChange={this.handleChange}
                    onCommit={this.handleCommit}
                    onRevert={this.handleRevert}
                    onKeyDown={this.handleKey}
                />
      );
    }
    return undefined;
  }

  renderViewer(cell: GridElement, row: number, col: number, valueRenderer: ValueRenderer<GridElement>, valueViewer: ValueViewer<GridElement>) {
    const Viewer = cell.valueViewer || valueViewer || DefaultValueViewer;
    const value = renderValue(cell, row, col, valueRenderer);
    return <Viewer cell={cell} row={row} col={col} value={value} />;
  }

  render() {
    const {
      row,
      col,
      cell,
      cellRenderer: Cells,
      valueRenderer,
      dataEditor,
      valueViewer,
      attributesRenderer,
      selected,
      editing,
      onKeyUp,
    } = this.props;
    const { updated } = this.state;

    const content = this.renderComponent(editing, cell)
            || this.renderEditor(editing, cell, row, col, dataEditor)
            || this.renderViewer(cell, row, col, valueRenderer, valueViewer);

    const className = [
      cell.className,
      'cell',
      cell.overflow,
      selected && 'selected',
      editing && 'editing',
      cell.readOnly && 'read-only',
      updated && 'updated',
    ]
      .filter((a) => a)
      .join(' ');

    return (
            <Cells
                row={row}
                col={col}
                cell={cell}
                selected={selected}
                editing={editing}
                updated={updated}
                attributesRenderer={attributesRenderer}
                className={className}
                style={widthStyle(cell)}
                onMouseDown={this.handleMouseDown}
                onMouseOver={this.handleMouseOver}
                onDoubleClick={this.handleDoubleClick}
                onContextMenu={this.handleContextMenu}
                onKeyUp={onKeyUp}
            >
                {content}
            </Cells>
    );
  }
}

